import React, {useState} from "react";
import {Tooltip, Modal, Select} from "antd";
import {useObserver} from "mobx-react-lite";
import moment from "moment";
import {
    ClockCircleOutlined,
    UserSwitchOutlined,
    DeleteOutlined,
    HistoryOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import {ResponsiveDataTable} from "../../_defaultComponents/ResponsiveDataTable";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {ShiftStatusTag} from "../../_defaultComponents/ShiftStatusTag/ShiftStatusTag";
import {LateStartIcon, EarlyEndIcon} from "../../shiftScore/Components/ShiftScoreTableStyled";
import {useShiftScoreActions} from "../../shiftScore/Components/useShiftScoreActions";
import {minuteToString} from "../../../utils/helpers";
import {ActionButtons, TableContainer} from "./ComponentStyles";
import {TimeChangeModal, CourierChangeModal} from "./PoolShiftChangeModals";
import ShiftUpdateModal from "./ShiftUpdateModal/ShiftUpdateModal";
import ShiftCourierUpdateModal from "./ShiftCourierUpdateModal/ShiftCourierUpdateModal";
import {getChangeReasonLabel, toShiftReportDetailShape} from "./normalizeShiftRow";
import {isPoolWorkType} from "../../../constants/shiftWorkTypes";
import {toShiftReportStatusCode} from "../../../constants/shiftStatuses";

const cancellationReasons = [
    {value: "kaza", label: "Kaza"},
    {value: "uyumsuzluk", label: "Uyumsuzluk"},
    {value: "hatali_vardiya", label: "Hatalı Vardiya"},
    {value: "saglik", label: "Sağlık"},
    {value: "erken_giris", label: "Erken Giriş"}
];

const changeHistoryActions = {
    shift_added: "Vardiya Eklendi",
    time_change: "Saat Değiştirildi",
    courier_change: "Kurye Değiştirildi",
    shift_canceled: "Vardiya İptal Edildi"
};

const PAGE_SIZE = 10;

// Süre ve sayı kolonlarında boş değer gösterilmiyor: uçlardan biri alanı hiç döndürmediğinde
// de kolon 0 (süreler "0 dk") basıyor.
const renderMinutes = minutes => minuteToString(Number(minutes) || 0);

const renderCount = count => (count === null || count === undefined ? "-" : count);

/**
 * Havuz ve dedike vardiyaların ortak tablosu. Satırlar `normalizeShiftRow` şemasında gelir;
 * aksiyon kolonu satırın kaynağına göre üç setten birini gösterir: havuz (saat/kurye değiştir,
 * iptal, geçmiş), dedike vardiya (saat/kurye değiştir, sil) veya puantaj raporu (rapor detayı,
 * onay, itiraz). Puantaj seti yalnız rapor listesiyle beslenen ekranda anlamlı olduğu için
 * `useReportActions` ile açılır.
 */
const ShiftManagementTable = ({
    loading,
    data,
    currentPage,
    setCurrentPage,
    dataCount,
    setReloadData,
    workType,
    useReportActions = false,
    hideVerifiedPackageCount = false,
    hideActionsColumn = false,
    hideStatusColumn = false
}) => {
    const stores = useStore();
    const isPoolShift = isPoolWorkType(workType);
    const [timeModalVisible, setTimeModalVisible] = useState(false);
    const [courierModalVisible, setCourierModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [changeHistoryModalVisible, setChangeHistoryModalVisible] = useState(false);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedShift, setSelectedShift] = useState(null);
    const [cancelNote, setCancelNote] = useState(null);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [changeHistory, setChangeHistory] = useState([]);
    const [shiftToUpdateTime, setShiftToUpdateTime] = useState(null);
    const [shiftToUpdateCourier, setShiftToUpdateCourier] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const {
        renderActionButtons: renderShiftScoreActions,
        actionModals,
        isDedicatedIntegration
    } = useShiftScoreActions({
        setReloadData,
        onApproved: () => setReloadData()
    });

    // Aksiyon kolonunun görünürlüğü eski tablolardaki kurallarla birebir aynı tutuluyor:
    // her tipte seller gizli (ShiftTable/PoolShiftTable), puantaj aksiyonlarında ayrıca
    // dedike entegrasyon müşterisi ve operationSupport gizli (ShiftScoreTable).
    const hideActions =
        hideActionsColumn ||
        stores.meStore.checkRole("isSeller") ||
        (useReportActions &&
            (isDedicatedIntegration || stores.meStore.checkRole("hideForOperationRole")));

    const canUpdateShift = stores.meStore.checkPermission("shiftManagement.updateShift");
    const canShiftReport = stores.meStore.checkPermission("shiftManagement.shiftReport");

    const [, shiftHours] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolShiftStore.getShiftHours.bind(null, {}),
        dependencies: [workType],
        initialCall: isPoolShift,
        condition: isPoolShift
    });

    const setShiftData = record => {
        setSelectedCourier(record.courier);
        setSelectedShift({
            shiftDate: record.date,
            shiftName: record.shiftName,
            poolId: record.shiftId,
            plannedTime: record.plannedTime,
            startHourId: record.startHourId,
            endHourId: record.endHourId,
            plannedStartTime: record.plannedStartTime,
            plannedEndTime: record.plannedEndTime
        });
    };

    const handleTimeChange = record => {
        setShiftData(record);
        setTimeModalVisible(true);
    };

    const handleCourierChange = record => {
        setShiftData(record);
        setCourierModalVisible(true);
    };

    const handleDelete = record => {
        setRecordToDelete(record);
        setCancelModalVisible(true);
    };

    const handleChangeHistory = async record => {
        setChangeHistoryModalVisible(true);
        const poolShiftSyChangesList = await stores.poolShiftStore.getPoolShiftSyChangeLogs({
            poolShiftSyChangeLogIds: record.changedIds
        });
        setChangeHistory(poolShiftSyChangesList.data);
    };

    const handleConfirmDelete = async () => {
        if (recordToDelete && cancelNote) {
            await stores.poolShiftStore.cancelCourierPoolShift({
                courierId: recordToDelete.courier._id,
                poolShiftForecastId: recordToDelete.poolShiftForecastId,
                reason: cancelNote,
                startHourId: recordToDelete.startHourId,
                endHourId: recordToDelete.endHourId
            });
            setCancelModalVisible(false);
            setReloadData();
            setCancelNote(null);
            setRecordToDelete(null);
        }
    };

    const handleCancelModal = () => {
        setCancelModalVisible(false);
        setCancelNote(null);
        setRecordToDelete(null);
    };

    const renderPoolActions = record => {
        if (!canUpdateShift) return null;
        const startTime = moment(record.date + " " + record.plannedStartTime, "YYYY-MM-DD HH:mm");
        let endTime = moment(record.date + " " + record.plannedEndTime, "YYYY-MM-DD HH:mm");
        if (endTime.isBefore(startTime)) {
            endTime.add(1, "day");
        }
        const deleteVisibility = stores.meStore.checkRole("isAdmin") && !record.cancelNote;
        const isBeforeCurrentTime = endTime.isBefore(moment());
        const changeHistoryVisibility = record.cancelNote;

        return (
            <ActionButtons>
                <Tooltip title={"Saat Değiştir"}>
                    <CustomButton
                        data-testid="changeTimeButton"
                        onClick={() => handleTimeChange(record)}
                        icon={<ClockCircleOutlined />}
                        disabled={record.cancelNote || isBeforeCurrentTime}
                    />
                </Tooltip>

                <Tooltip title={"Kurye Değiştir"}>
                    <CustomButton
                        data-testid="changeCourierButton"
                        onClick={() => handleCourierChange(record)}
                        icon={<UserSwitchOutlined />}
                        disabled={record.cancelNote || isBeforeCurrentTime}
                    />
                </Tooltip>

                {deleteVisibility && (
                    <Tooltip title={"Sil"}>
                        <CustomButton
                            data-testid="courierShiftDeleteButton"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            disabled={record.cancelNote || isBeforeCurrentTime}
                        />
                    </Tooltip>
                )}

                {changeHistoryVisibility && (
                    <Tooltip title={"Değişim Geçmişi"}>
                        <CustomButton
                            data-testid="courierShiftHistoryButton"
                            icon={<HistoryOutlined />}
                            onClick={() => handleChangeHistory(record)}
                        />
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const closeDedicatedModal = (setSelected, reloadData) => {
        if (reloadData) setReloadData();
        setSelected(null);
    };

    const handleDedicatedDelete = record => {
        stores.appStore.openConfirmModal({
            title: "Vardiya Silme",
            content: "Bu vardiyayı silmek istediğinizden emin misiniz?",
            onOk: async () => {
                setDeleteLoading(record._id);
                await stores.shiftStore.deleteShift({_id: record._id});
                setDeleteLoading(false);
                setReloadData();
            }
        });
    };

    /* Vardiya (sabit) aksiyonlarının görünürlük kuralları eski ShiftTable ile aynı: bitmiş
       vardiyanın saati değiştirilemez, kurye değişimi vardiya başlamadan 3 dakika öncesine
       kadar yapılabilir, silme yalnız adminde. Vardiya tamamlanıp puantaj akışına girdiğinde
       (rapor oluşmuş ya da bitiş saati geçmiş) silme gösterilmez. */
    const renderDedicatedActions = record => {
        const shiftStart = moment(record.shiftStartTime, "YYYY-MM-DD HH:mm:ss");
        const shiftEnd = moment(record.shiftEndTime, "YYYY-MM-DD HH:mm:ss");
        const canUpdateTime = shiftEnd.isSameOrAfter(moment());
        const canUpdateCourier = shiftStart
            .clone()
            .subtract(3, "m")
            .isAfter(moment());
        const isShiftCompleted = !canUpdateTime || Boolean(record.reportId);

        return (
            <ActionButtons>
                {stores.meStore.checkRole("isAdmin") && !isShiftCompleted && (
                    <Tooltip title={"Sil"}>
                        <CustomButton
                            data-testid="deleteButton"
                            disabled={Boolean(deleteLoading)}
                            loading={deleteLoading === record._id}
                            onClick={() => handleDedicatedDelete(record)}
                            icon={<DeleteOutlined />}
                        />
                    </Tooltip>
                )}

                {canUpdateTime && canUpdateShift && (
                    <Tooltip title={"Saat Değiştir"}>
                        <CustomButton
                            data-testid="changeTimeButton"
                            onClick={() => setShiftToUpdateTime(record)}
                            icon={<ClockCircleOutlined />}
                        />
                    </Tooltip>
                )}

                {canUpdateCourier && canUpdateShift && (
                    <Tooltip title={"Kurye Değiştir"}>
                        <CustomButton
                            data-testid="changeCourierButton"
                            // Modal firma/şubeyi nesne olarak okuyor; liste ucu yalnız adları
                            // döndürdüğü için burada aynı şekle sarılıyor.
                            onClick={() =>
                                setShiftToUpdateCourier({
                                    ...record,
                                    company: {companyName: record.companyName},
                                    subcompany: {name: record.subCompanyName}
                                })
                            }
                            icon={<UserSwitchOutlined />}
                        />
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const renderReportActionsFromShiftRow = row =>
        renderShiftScoreActions(row.reportId, {
            ...toShiftReportDetailShape(row),
            status: toShiftReportStatusCode(row.status)
        });

    /* Geç başlama / erken bitiş ikonları yalnız puantaj kayıtlarında dolu geliyor. */
    const renderShiftDeviationIcon = (minutes, label, Icon) =>
        stores.meStore.checkRole("notVisibleForCompanyUser") && minutes > 0 ? (
            <Tooltip title={`${minutes} dk ${label}`}>
                <Icon style={{marginLeft: "5px"}} />
            </Tooltip>
        ) : null;

    const withLabel = title => () => ({"data-label": title});

    const columns = [
        {
            title: "Tarih",
            dataIndex: "date",
            key: "date",
            onCell: withLabel("Tarih"),
            render: date => <span data-testid="date">{date || "-"}</span>
        },
        {
            title: "Proje",
            dataIndex: "projectName",
            key: "projectName",
            onCell: withLabel("Proje"),
            render: projectName => <span data-testid="projectName">{projectName || "-"}</span>
        },
        {
            title: "Kurye Adı",
            dataIndex: "courier",
            key: "courierName",
            onCell: withLabel("Kurye Adı"),
            render: courier => (
                <span data-testid="courierName">
                    {stores.meStore.checkRole("courierDetail") ? (
                        <CourierLink target="_blank" to={`/couriers/${courier?._id}`}>
                            {courier?.fullname}
                        </CourierLink>
                    ) : (
                        courier?.fullname
                    )}
                </span>
            )
        },
        {
            title: "Kimlik Numarası",
            dataIndex: "courier",
            key: "courierTckn",
            onCell: withLabel("Kimlik Numarası"),
            render: courier => <span data-testid="courierTckn">{courier?.tckn || "-"}</span>
        },
        {
            title: "Şehir",
            dataIndex: "city",
            key: "city",
            onCell: withLabel("Şehir"),
            render: city => <span data-testid="city">{city ? city.capitalize() : "-"}</span>
        },
        {
            title: "Planlanan Saat",
            dataIndex: "plannedTime",
            key: "plannedTime",
            onCell: withLabel("Planlanan Saat"),
            render: plannedTime => <span data-testid="plannedTime">{plannedTime || "-"}</span>
        },
        {
            title: "Gerçekleşen Saat",
            dataIndex: "actualTime",
            key: "actualTime",
            onCell: withLabel("Gerçekleşen Saat"),
            render: (actualTime, record) => (
                <span data-testid="actualTime">
                    {actualTime || "-"}
                    {renderShiftDeviationIcon(record.lateStart, "geç başlangıç", LateStartIcon)}
                    {renderShiftDeviationIcon(record.earlyEnd, "erken bitiş", EarlyEndIcon)}
                </span>
            )
        },
        {
            title: "Planlanan Çalışma",
            dataIndex: "plannedWorkTimeMinute",
            key: "plannedWorkTimeMinute",
            onCell: withLabel("Planlanan Çalışma"),
            render: minutes => <span data-testid="plannedWorkTime">{renderMinutes(minutes)}</span>
        },
        {
            title: "Çalışma Saati",
            dataIndex: "workTimeMinute",
            key: "workTimeMinute",
            onCell: withLabel("Çalışma Saati"),
            render: minutes => <span data-testid="workTime">{renderMinutes(minutes)}</span>
        },
        {
            title: "Değişiklik Sebebi",
            dataIndex: "changeReason",
            key: "changeReason",
            onCell: withLabel("Değişiklik Sebebi"),
            render: changeReason => <span data-testid="changeReason">{changeReason || "-"}</span>
        },
        {
            title: "Paket Sayısı",
            dataIndex: "packageCount",
            key: "packageCount",
            onCell: withLabel("Paket Sayısı"),
            render: (packageCount, record) => (
                <span data-testid="packageCount">
                    {renderCount(packageCount)}
                    {stores.meStore.checkRole("notVisibleForCompanyUser") && record?.reason && (
                        <Tooltip title={record.reason}>
                            {" "}
                            <InfoCircleOutlined />
                        </Tooltip>
                    )}
                </span>
            )
        },
        {
            title: "Onaylanmış Paket Sayısı",
            dataIndex: "verifiedPackageCount",
            key: "verifiedPackageCount",
            hidden: hideVerifiedPackageCount,
            onCell: withLabel("Onaylanmış Paket Sayısı"),
            render: (verifiedPackageCount, record) => (
                <span data-testid="verifiedPackageCount">
                    {renderCount(verifiedPackageCount)}
                    {stores.meStore.checkRole("notVisibleForCompanyUser") && record?.reason && (
                        <Tooltip title={record.reason}>
                            {" "}
                            <InfoCircleOutlined />
                        </Tooltip>
                    )}
                </span>
            )
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            hidden: hideStatusColumn,
            onCell: withLabel("Durum"),
            render: status => <ShiftStatusTag status={status} />
        },
        {
            title: "Aksiyon",
            key: "action",
            hidden: hideActions,
            onCell: withLabel("Aksiyon"),
            render: (_, record) => {
                if (isPoolShift) return renderPoolActions(record.raw);
                if (useReportActions) {
                    return canShiftReport
                        ? renderShiftScoreActions(record.raw?._id, record.raw)
                        : null;
                }
                // Dedike sekmesi: raporu oluşmuş (reportId + puantaj durumu) vardiyada puantaj
                // aksiyonları gösterilir; aksi halde vardiya (saat/kurye/sil) aksiyonları.
                const hasReport =
                    Boolean(record.raw?.reportId) &&
                    Boolean(toShiftReportStatusCode(record.raw?.status));
                return hasReport &&
                    canShiftReport &&
                    !stores.meStore.checkRole("hideForOperationRole")
                    ? renderReportActionsFromShiftRow(record.raw)
                    : renderDedicatedActions(record.raw);
            }
        }
    ];

    const changeHistoryColumns = [
        {
            title: "Güncelleme Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: createdAt => moment(createdAt).format("DD.MM.YYYY HH:mm")
        },
        {
            title: "Aksiyon",
            dataIndex: "action",
            key: "action",
            render: (_, record) => changeHistoryActions[record.action]
        },
        {
            title: "Eski Değer",
            dataIndex: "oldValue",
            key: "oldValue"
        },
        {
            title: "Yeni Değer",
            dataIndex: "newValue",
            key: "newValue"
        },
        {
            title: "Değişim Sebebi",
            dataIndex: "reason",
            key: "reason",
            render: reason => getChangeReasonLabel(reason)
        },
        {
            title: "Güncelleme Yapan Kişi",
            dataIndex: "createdBy",
            key: "createdBy"
        }
    ];

    return useObserver(() => (
        <>
            {(useReportActions || !isPoolShift) && actionModals}
            <ResponsiveDataTable
                size="middle"
                loading={loading}
                columns={columns.filter(column => !column.hidden)}
                dataSource={data}
                // `dataCount` verilirse (sabit liste) sunucu sayfalaması yapılır: antd yalnız o
                // sayfanın satırlarını alır, toplam sayfa sayısını `total` alanından hesaplar.
                // Verilmezse (havuz/entegrasyon) veri tek istekte geldiği için antd kendi sayfalar.
                pagination={{
                    current: currentPage,
                    pageSize: PAGE_SIZE,
                    total: dataCount,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    onChange: page => setCurrentPage(page)
                }}
                display={data.length > 0 ? "grid" : "flex"}
                scroll={{x: "max-content"}}
                rowKey="rowKey"
                data-testid="shiftManagementTable"
            />

            {!isPoolShift && !useReportActions && (
                <>
                    <ShiftUpdateModal
                        id={shiftToUpdateTime?._id}
                        courierName={shiftToUpdateTime?.courier?.fullname}
                        visibility={Boolean(shiftToUpdateTime?._id)}
                        date={moment(shiftToUpdateTime?.date)}
                        startTime={moment(shiftToUpdateTime?.shiftStartTime, "YYYY-MM-DD HH:mm:ss")}
                        endTime={moment(shiftToUpdateTime?.shiftEndTime, "YYYY-MM-DD HH:mm:ss")}
                        onCancel={reloadData =>
                            closeDedicatedModal(setShiftToUpdateTime, reloadData)
                        }
                    />

                    <ShiftCourierUpdateModal
                        shift={shiftToUpdateCourier}
                        visibility={Boolean(shiftToUpdateCourier?._id)}
                        onCancel={reloadData =>
                            closeDedicatedModal(setShiftToUpdateCourier, reloadData)
                        }
                    />
                </>
            )}

            {isPoolShift && (
                <>
                    <TimeChangeModal
                        visible={timeModalVisible}
                        onCancel={() => setTimeModalVisible(false)}
                        courier={selectedCourier}
                        shiftData={selectedShift}
                        shiftHours={shiftHours?.data || []}
                        onSuccess={() => setReloadData()}
                    />

                    <CourierChangeModal
                        visible={courierModalVisible}
                        onCancel={() => setCourierModalVisible(false)}
                        courier={selectedCourier}
                        shiftData={selectedShift}
                        shiftHours={shiftHours?.data || []}
                        onSuccess={() => setReloadData()}
                    />

                    <Modal
                        title="Vardiya İptali"
                        visible={cancelModalVisible}
                        onOk={handleConfirmDelete}
                        onCancel={handleCancelModal}
                        okText="İptal Et"
                        cancelText="Vazgeç"
                        okButtonProps={{disabled: !cancelNote}}
                    >
                        <p>Bu vardiyayı iptal etmek istediğinizden emin misiniz?</p>
                        <Select
                            style={{width: "100%", marginTop: "16px"}}
                            placeholder="İptal sebebini seçin"
                            value={cancelNote}
                            onChange={setCancelNote}
                            options={cancellationReasons}
                        />
                    </Modal>

                    <Modal
                        title="Değişim Geçmişi"
                        visible={changeHistoryModalVisible}
                        onCancel={() => setChangeHistoryModalVisible(false)}
                        cancelText="Kapat"
                        okButtonProps={{style: {display: "none"}}}
                        width={1000}
                    >
                        <TableContainer
                            dataSource={changeHistory}
                            columns={changeHistoryColumns}
                            locale={{emptyText: <EmptyMessage />}}
                            pagination={false}
                            display={changeHistory.length > 0 ? "grid" : "flex"}
                            scroll={{x: "max-content"}}
                            rowKey="_id"
                            dataTitles={[
                                "Güncelleme Tarihi",
                                "Aksiyon",
                                "Eski Değer",
                                "Yeni Değer",
                                "Değişim Sebebi",
                                "Güncelleme Yapan Kişi"
                            ]}
                        />
                    </Modal>
                </>
            )}
        </>
    ));
};

export default ShiftManagementTable;
